import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchCatalog, fetchTopic, getYoutubeEmbedUrl, buildTopicChildrenMap, updateQuizDisplayMode, updateQuizClosed } from "./quiz3Utils";
import Quiz3TreeBrowser from "./Quiz3TreeBrowser";
import { useResizableWidth, ResizeDivider } from "./useResizableWidth";
import { getHsUid } from "../happyschool_static/hsAuth";

// Dòng "![chú thích](đường-dẫn)" trong lý thuyết = ảnh minh họa (đường dẫn bắt
// đầu bằng "/" — file trong public/ — hoặc "https://"); các dòng còn lại là chữ.
const THEORY_IMG_LINE = /^!\[([^\]]*)\]\(((?:\/|https?:\/\/)[^)\s]+)\)\s*$/;

const TheoryContent = ({ text }) => {
  const blocks = [];
  let buf = [];
  const flush = () => { if (buf.length) { blocks.push({ type: "text", value: buf.join("\n") }); buf = []; } };
  String(text || "").split("\n").forEach((line) => {
    const m = line.match(THEORY_IMG_LINE);
    if (m) { flush(); blocks.push({ type: "img", alt: m[1], src: m[2] }); } else buf.push(line);
  });
  flush();
  return blocks.map((b, i) =>
    b.type === "img" ? (
      <figure key={i} style={{ margin: "14px 0", textAlign: "center" }}>
        <img src={b.src} alt={b.alt} loading="lazy" style={{ maxWidth: "100%", height: "auto", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }} />
        {b.alt && <figcaption style={{ marginTop: 4, fontSize: 13, color: "#6b7280" }}>{b.alt}</figcaption>}
      </figure>
    ) : (
      <p key={i} className="quiz3-theory-content" style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#374151" }}>
        {b.value}
      </p>
    )
  );
};

// TQ-01 — duyệt cây môn học/chủ đề, chọn 1 node để xem đề cố định/đề tổng
// hợp gắn đúng node đó (TQ-02 / TQ-02b).
const Quiz3TakeQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { width: treeWidth, onDragStart } = useResizableWidth(280, 180, 720);
  // Cây môn học/chủ đề chiếm nhiều màn hình trên điện thoại -> cho ẩn/hiện.
  // Luôn mặc định HIỆN (cần thấy cây để chọn bài lúc đầu) — người dùng tự bấm
  // ẩn đi sau khi đã chọn xong để có thêm chỗ xem nội dung/làm bài.
  const [treeOpen, setTreeOpen] = useState(true);
  const [quizzes, setQuizzes] = useState({});
  const [topics, setTopics] = useState({});
  // Nếu tới từ nút "Học lại" (mang theo subjectId/topicId), tự chọn sẵn node đó
  // để hiện ngay lý thuyết + danh sách đề của chủ đề.
  const [selected, setSelected] = useState(() => {
    const s = location.state;
    if (s && s.subjectId) {
      return { subjectId: s.subjectId, topicId: s.topicId || null, key: s.topicId ? `topic:${s.topicId}` : `subject:${s.subjectId}` };
    }
    return { subjectId: null, topicId: null, key: null };
  });

  useEffect(() => {
    let cancelled = false;
    // Catalog nhẹ (không kèm câu hỏi, không kèm lý thuyết) thay cho /api/all.
    fetchCatalog()
      .then((data) => {
        if (cancelled) return;
        setQuizzes(data.quizzes || {});
        setTopics(data.topics || {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Lý thuyết/video chỉ tải cho ĐÚNG chủ đề đang chọn (catalog không kèm — chỉ
  // có cờ hasTheory/hasVideo). Server cũ (lùi về /api/all) thì đã có sẵn trong topics.
  const [topicDetail, setTopicDetail] = useState(null); // { id, theory, video }
  useEffect(() => {
    const tid = selected.topicId;
    if (!tid) { setTopicDetail(null); return; }
    const t = topics[tid];
    if (!t || t.theory !== undefined || (!t.hasTheory && !t.hasVideo)) { setTopicDetail(null); return; }
    let cancelled = false;
    fetchTopic(tid)
      .then((full) => { if (!cancelled) setTopicDetail({ id: tid, theory: full && full.theory, video: full && full.video }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selected.topicId, topics]);

  const selectedTopic = selected.topicId
    ? { ...topics[selected.topicId], ...(topicDetail && topicDetail.id === selected.topicId ? topicDetail : {}) }
    : null;
  const theory = selectedTopic ? selectedTopic.theory : null;
  const videoEmbedUrl = selectedTopic ? getYoutubeEmbedUrl(selectedTopic.video) : null;

  const byParent = useMemo(() => buildTopicChildrenMap(topics), [topics]);

  const quizzesAtNode = useMemo(() => {
    if (!selected.subjectId) return [];
    const direct = Object.entries(quizzes)
      .map(([id, q]) => ({ id, ...q }))
      .filter((q) => q.subjectId === selected.subjectId)
      .filter((q) => (q.topicId || null) === (selected.topicId || null))
      // Đề đã đóng chỉ người tạo đề còn thấy (để in / mở lại).
      .filter((q) => !q.closed || q.createdByUid === getHsUid());

    // "Làm bài tổng hợp" (đề loại synthetic) vẫn LƯU topicId = topic CHA
    // (để bốc câu hỏi ngẫu nhiên đúng phạm vi toàn bộ mục con — xem
    // getDescendantTopicIds ở Quiz3QuizPlayer.js/server.js) nhưng chỉ nên
    // HIỆN RA ở mục con CUỐI CÙNG — hợp lý hơn vì học sinh chỉ nên làm bài
    // tổng hợp sau khi đã học hết các mục con, không phải ngay khi vừa mở
    // mục cha. Nên nếu node đang chọn là con cuối cùng của cha nó, gộp
    // thêm các đề tổng hợp gắn ở cha vào danh sách hiển thị tại đây.
    if (!selected.topicId) return direct;
    const currentTopic = topics[selected.topicId];
    const parentId = currentTopic ? currentTopic.parentTopicId : null;
    if (!parentId) return direct;
    const siblings = byParent[parentId] || [];
    const isLastChild = siblings.length > 0 && siblings[siblings.length - 1].id === selected.topicId;
    if (!isLastChild) return direct;

    const parentSynthetic = Object.entries(quizzes)
      .map(([id, q]) => ({ id, ...q }))
      .filter((q) => q.subjectId === selected.subjectId && q.type === "synthetic" && q.topicId === parentId);
    return [...direct, ...parentSynthetic];
  }, [quizzes, selected, topics, byParent]);

  // Bài kế tiếp theo đúng thứ tự cây (DFS các topic đã sắp xếp trong cùng môn),
  // dùng cho nút "Học tiếp". null nếu đang ở bài cuối cùng.
  const nextTopicId = useMemo(() => {
    if (!selected.subjectId || !selected.topicId) return null;
    const ordered = [];
    const walk = (key) => {
      (byParent[key] || []).forEach((child) => {
        ordered.push(child.id);
        walk(child.id);
      });
    };
    walk(`root:${selected.subjectId}`);
    const idx = ordered.indexOf(selected.topicId);
    return idx >= 0 && idx < ordered.length - 1 ? ordered[idx + 1] : null;
  }, [selected, byParent]);

  const changeDisplayMode = async (quizId, displayMode) => {
    try {
      await updateQuizDisplayMode(quizId, displayMode);
      setQuizzes((prev) => ({ ...prev, [quizId]: { ...prev[quizId], displayMode } }));
    } catch (err) {
      window.alert(err.message || "Không đổi được chế độ hiển thị.");
    }
  };

  const toggleClosed = async (quizId, closed) => {
    try {
      await updateQuizClosed(quizId, closed);
      setQuizzes((prev) => ({ ...prev, [quizId]: { ...prev[quizId], closed } }));
    } catch (err) {
      window.alert(err.message || "Không đổi được trạng thái đề.");
    }
  };

  const goToTopic = (topicId) => {
    setSelected({ subjectId: selected.subjectId, topicId, key: `topic:${topicId}` });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Xuất toàn bộ lý thuyết của 1 chủ đề + mọi mục con (đúng thứ tự cây) ra
  // file .doc — dùng "trick" HTML+mimetype application/msword, Word mở được
  // bình thường, không cần cài thêm thư viện.
  const exportTheoryDoc = async (rootTopicId, docTitle) => {
    const byParent = buildTopicChildrenMap(topics);
    const orderedIds = [rootTopicId];
    const walk = (id) => {
      (byParent[id] || []).forEach((child) => {
        orderedIds.push(child.id);
        walk(child.id);
      });
    };
    walk(rootTopicId);

    const esc = (s) =>
      String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    // Catalog không kèm lý thuyết -> tải đúng các mục có lý thuyết (song song)
    // ngay lúc bấm xuất file. Server cũ thì topics đã có sẵn theory.
    const fullTopics = await Promise.all(
      orderedIds.map(async (id) => {
        const t = topics[id];
        if (!t || t.theory !== undefined || !t.hasTheory) return t;
        try { return { ...t, ...(await fetchTopic(id)) }; } catch (e) { return t; }
      })
    );

    const sections = fullTopics
      .filter((t) => t && t.theory)
      .map(
        (t) =>
          `<h2>${esc(t.name)}</h2><p style="white-space:pre-wrap;line-height:1.5;">${esc(String(t.theory).split("\n").map((l) => { const m = l.match(THEORY_IMG_LINE); return m ? `[Hình: ${m[1] || "minh họa"}]` : l; }).join("\n"))}</p>`
      )
      .join("\n");

    if (!sections) {
      window.alert("Chưa có nội dung lý thuyết nào ở mục này để xuất file.");
      return;
    }

    const html = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head><meta charset="utf-8"><title>${esc(docTitle)}</title></head>
<body><h1>${esc(docTitle)}</h1>${sections}</body>
</html>`;

    const blob = new Blob(["﻿", html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${docTitle}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px 0" }}>Làm bài</h2>
      <p style={{ color: "#6b7280", marginTop: 0, marginBottom: 12 }}>
        Chọn 1 môn học/chủ đề ở cây bên trái để xem các đề gắn với node đó.
      </p>

      <button type="button" onClick={() => setTreeOpen((o) => !o)} style={toggleTreeBtnStyle}>
        {treeOpen ? "◂ Ẩn danh mục" : "☰ Hiện danh mục"}
      </button>

      <div style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
        {treeOpen && (
          <>
            <div style={{ width: treeWidth, flexShrink: 0, overflow: "auto" }}>
              <Quiz3TreeBrowser
                selectedKey={selected.key}
                onSelectNode={(subjectId, topicId) =>
                  setSelected({ subjectId, topicId, key: topicId ? `topic:${topicId}` : `subject:${subjectId}` })
                }
              />
            </div>

            {/* Thanh chia dọc kéo được giữa tree và box phải */}
            <ResizeDivider onDragStart={onDragStart} />
          </>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {!selected.subjectId && <p style={{ color: "#6b7280" }}>Chưa chọn môn học/chủ đề nào.</p>}

          {theory && (
            <div style={theoryBoxStyle}>
              {/* .grid-container > div ở App.css ép text-align: center !important với độ đặc
                  hiệu (0,1,1) — cao hơn 1 class thường (0,1,0), nên phải nhân đôi class mới
                  thắng được; chỉ áp cho nội dung lý thuyết, giữ nguyên tiêu đề căn giữa. */}
              <style>{`
                .quiz3-theory-content.quiz3-theory-content {
                  text-align: left !important;
                }
              `}</style>
              <h4 style={{ margin: "0 0 8px 0" }}>📖 Lý thuyết</h4>
              <TheoryContent text={theory} />
            </div>
          )}

          {videoEmbedUrl && (
            <div style={{ ...theoryBoxStyle, padding: 12 }}>
              <h4 style={{ margin: "0 0 8px 4px" }}>🎬 Video bài giảng</h4>
              <div style={{ aspectRatio: "16 / 9", maxWidth: 640 }}>
                <iframe
                  src={videoEmbedUrl}
                  title="Video bài giảng"
                  style={{ width: "100%", height: "100%", border: "none", borderRadius: 8 }}
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {selected.subjectId && quizzesAtNode.length === 0 && (
            <p style={{ color: "#6b7280" }}>Chưa có đề nào gắn với node này.</p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {quizzesAtNode.map((q) => (
              <div key={q.id} style={quizCardStyle}>
                <div>
                  <p style={{ margin: "0 0 4px 0", fontWeight: 600 }}>
                    {q.title} <span style={badgeStyle}>{q.type === "fixed" ? "Đề cố định" : "Đề tổng hợp"}</span>
                    {q.closed && <span style={{ ...badgeStyle, background: "#fef2f2", color: "#dc2626" }}>🔒 Đã đóng</span>}
                  </p>
                  <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                    {q.numQuestions} câu
                    {q.timeLimitSeconds ? ` · ${Math.round(q.timeLimitSeconds / 60)} phút` : " · không giới hạn thời gian toàn đề"}
                    {q.timePerQuestionSeconds ? ` · ${q.timePerQuestionSeconds}s/câu` : ""}
                    {` · ${q.displayMode === "animated" ? "Chống chụp ảnh AI" : "Hiện thường"}`}
                  </p>
                  {q.createdByUid === getHsUid() && (
                    <label style={{ fontSize: 13, color: "#374151", display: "inline-block", marginTop: 6 }}>
                      Chế độ hiển thị:{" "}
                      <select
                        value={q.displayMode === "animated" ? "animated" : "both"}
                        onChange={(e) => changeDisplayMode(q.id, e.target.value)}
                      >
                        <option value="both">Hiện thường</option>
                        <option value="animated">Chống chụp ảnh AI</option>
                      </select>
                    </label>
                  )}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {q.type === "synthetic" && q.topicId && q.poolScope !== "subject" && (
                    <button
                      style={secondaryBtnStyle}
                      onClick={() => exportTheoryDoc(q.topicId, topics[q.topicId]?.name || q.title)}
                      title="Xuất toàn bộ lý thuyết của mục này + các mục con ra file .doc"
                    >
                      📄 Export lý thuyết
                    </button>
                  )}
                  {q.type === "fixed" && (
                    <>
                      <button
                        style={secondaryBtnStyle}
                        onClick={() => navigate(`/quiz3/print/${q.id}`)}
                        title="In đề + phiếu trả lời để làm trên giấy"
                      >
                        🖨 In phiếu trả lời
                      </button>
                      {q.createdByUid === getHsUid() && (
                        <button
                          style={secondaryBtnStyle}
                          onClick={() => navigate(`/quiz3/answerkey/${q.id}`)}
                          title="In đáp án đúng của đề này (chỉ người tạo đề xem được)"
                        >
                          🗝 In đáp án
                        </button>
                      )}
                      {!q.closed && (
                        <button
                          style={secondaryBtnStyle}
                          onClick={() => navigate(`/quiz3/scan/${q.id}`)}
                          title="Chụp ảnh phiếu trả lời đã tô để nộp bài"
                        >
                          📷 Nộp bài bằng ảnh
                        </button>
                      )}
                    </>
                  )}
                  {q.createdByUid === getHsUid() && (
                    <button
                      style={secondaryBtnStyle}
                      onClick={() => toggleClosed(q.id, !q.closed)}
                      title={q.closed ? "Mở lại cho học sinh thấy và làm online" : "Ẩn đề với học sinh, không cho làm online (vẫn in được)"}
                    >
                      {q.closed ? "🔓 Mở lại đề" : "🔒 Đóng đề"}
                    </button>
                  )}
                  {!q.closed && (
                    <button style={primaryBtnStyle} onClick={() => navigate(`/quiz3/play/${q.id}`)}>
                      Bắt đầu
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {selected.topicId && nextTopicId && (
            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
              <button style={primaryBtnStyle} onClick={() => goToTopic(nextTopicId)}>
                Học tiếp: {topics[nextTopicId]?.name} →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const toggleTreeBtnStyle = {
  padding: "6px 12px",
  marginBottom: 12,
  background: "#fff",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 13,
};

const theoryBoxStyle = {
  border: "1px solid #bfdbfe",
  background: "#f0f7ff",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};

const quizCardStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: 14,
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  background: "#fff",
};

const badgeStyle = {
  fontSize: 11,
  fontWeight: 500,
  color: "#2563eb",
  background: "#eff6ff",
  padding: "2px 8px",
  borderRadius: 999,
  marginLeft: 8,
};

const primaryBtnStyle = {
  padding: "8px 16px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const secondaryBtnStyle = {
  padding: "8px 16px",
  background: "#fff",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

export default Quiz3TakeQuiz;
